import * as _ from "lodash-es";
import React, { ChangeEvent, Component } from "react";
import { Link } from "react-router-dom";
import IOrder from "../../../models/IOrder";
import ISupplierModel from "../../../models/ISupplierModel";
import IUserUI from "../../../models/IUserUI";
import UIService from "../../../services/UIService";
import UserService from "../../../services/UserService";
import { OwIconButton } from "../../shared/OwIconButton/OwIconButton";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwTitle from "../../shared/OwTitle/OwTitle";
import OwTranslate from "../../shared/OwTranslate/OwTranslate";
import moment from "moment";
import "./RecentOrdersWidget.scss";
import UserTypesEnum from "../../../models/UserTypesEnum";
import OwButton from "../../shared/OwButton/OwButton";
import styled from "styled-components";
import { OwFnInput } from "../../shared/OwFnInput/OwFnInput";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IOnGetOrdersDTO from "../../../models/DTO/IOnGetOrdersDTO";
import IGetOrdersDTO from "../../../models/DTO/IGetOrdersDTO";
import IVendorModel from "../../../models/IVendorModel";
import Typeahead from "../../shared/Typeahead/Typeahead";
import IGetVendorsDTO from "../../../models/DTO/IGetVendorsDTO";
import IOnGetVendorsDTO from "../../../models/DTO/IOnGetVendorsDTO";
import PaginationBar from "../../shared/PaginationBar/PaginationBar";
import { Socket } from "socket.io-client";
import SupplierService from "../../../services/SupplierService";
import AccessTypesEnum from "../../../models/AccessTypesEnum";

type Props = {
    userModel?: IUserUI;
    supplierModel?: ISupplierModel;
    vendorModel?: IVendorModel;
    recentOrders: IOrder[];
    inEditor?: boolean;
    removeWidget?: () => void;
    socket?: Socket;
    history: any;
};

type State = {
    showFilterOverlay: boolean;
    vendor_name: string;
    order: string;
    typeaheadVendors: IVendorModel[];
    selectedVendor?: IVendorModel;
    recentOrders: IOrder[];
    totalOrders: number;
    isActive: boolean;
    clearTypeahead: boolean;
    limit: number;
    offset: number;
    filterCount: number;
    limitArr: { value: number; isSelected: boolean }[];
    hasInitialSearch: boolean;
    po_no: string;
};

class RecentOrdersWidget extends Component<Props, State> {
    state: State = {
        showFilterOverlay: false,
        vendor_name: "",
        order: "",
        po_no: "",
        typeaheadVendors: [],
        recentOrders: this.props.recentOrders,
        totalOrders: this.props.recentOrders.length,
        isActive: false,
        hasInitialSearch: false,
        clearTypeahead: false,
        limit: 5,
        offset: 0,
        filterCount: 0,
        limitArr: [{ value: 5, isSelected: true }],
    };

    componentDidMount() {
        this.props.socket?.on(EndpointsEnum.OrderOnGetOrders, this.onGetOrders);
        this.props.socket?.on(EndpointsEnum.VendorsOnGetVendors, this.onGetVendors);
        this.props.socket?.on(EndpointsEnum.Notify, this.notify);
    }

    componentWillUnmount() {
        this.props.socket?.removeListener(EndpointsEnum.OrderOnGetOrders, this.onGetOrders);
        this.props.socket?.removeListener(EndpointsEnum.VendorsOnGetVendors, this.onGetVendors);
        this.props.socket?.removeListener(EndpointsEnum.Notify, this.notify);
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
        if (!_.isEqual(prevProps.recentOrders, this.props.recentOrders)) {
            this.setState({
                recentOrders: this.props.recentOrders,
                totalOrders: this.props.recentOrders.length,
            });
        }
    }

    notify = () => {
        this.setState({
            isActive: false,
        });
    };

    getQueryString = (): { queryString: string; filterCount: number } => {
        let str = "";
        let filterCount = 0;

        const queryArr: string[] = [];

        if (this.state.selectedVendor) {
            queryArr.push(`vendor=${this.state.selectedVendor.guid}`);
            filterCount++;
        }

        if (this.state.order.length) {
            queryArr.push(`order=${this.state.order}`);
            filterCount++;
        }

        if (this.state.po_no.length) {
            queryArr.push(`po_no=${this.state.po_no}`);
            filterCount++;
        }

        if (queryArr.length) {
            str = "?" + queryArr.join("&");
        }

        return { queryString: str, filterCount };
    };

    getOrders = () => {
        if (this.props.userModel && this.props.supplierModel && this.props.vendorModel && this.props.socket) {
            const { queryString, filterCount } = this.getQueryString();

            const payload: IGetOrdersDTO = {
                userGuid: this.props.userModel.guid,
                userToken: this.props.userModel.token,
                supplierGuid: this.props.supplierModel.guid,
                vendorGuid: this.props.vendorModel.guid,
                limit: this.state.limit,
                offset: this.state.offset,
                callMeta: {
                    view: "RecentOrdersWidget",
                    fn: "getOrders",
                },
            };

            if (queryString.length) {
                payload.queryString = queryString;
            }

            this.props.socket?.emit(EndpointsEnum.OrderGetOrders, payload);

            this.setState({
                isActive: true,
                hasInitialSearch: true,
                filterCount,
            });
        }
    };

    onGetOrders = (data: IOnGetOrdersDTO) => {
        this.setState({
            recentOrders: data.orders,
            totalOrders: data.totalOrders,
            isActive: false,
            showFilterOverlay: false,
        });
    };

    parseLastUpdated = (dateStr: string) => {
        return UserService.parseLastUpdated(dateStr);
    };

    toggleFilters = () => {
        this.setState({
            showFilterOverlay: !this.state.showFilterOverlay,
        });
    };

    typeaheadGetVendors = (value: string) => {
        if (this.props.userModel && this.props.supplierModel && this.props.socket) {
            const encodedTypeahead = encodeURIComponent(value);

            const payload: IGetVendorsDTO = {
                userGuid: this.props.userModel.guid,
                userToken: this.props.userModel.token,
                supplierGuid: this.props.supplierModel.guid,
                queryString: `name=${encodedTypeahead}`,
            };

            this.props.socket.emit(EndpointsEnum.VendorsGetVendors, payload);
        }
    };

    onGetVendors = (data: IOnGetVendorsDTO) => {
        const typeaheadVendors = data.supplierVendorTokens.map((vendorToken) => {
            return vendorToken.Vendor!;
        });

        this.setState({
            typeaheadVendors,
        });
    };

    handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.value;
        const name = e.target.name;

        const state: any = {
            [name]: value,
        };

        this.setState(state);
    };

    selectVendor = (vendorModel: IVendorModel) => {
        this.setState({
            selectedVendor: vendorModel,
        });
    };

    clearTypeahead = () => {
        this.setState({
            selectedVendor: undefined,
            clearTypeahead: false,
        });
    };

    resetFilters = () => {
        this.setState({
            recentOrders: this.props.recentOrders,
            selectedVendor: undefined,
            order: "",
            showFilterOverlay: false,
            hasInitialSearch: false,
        });
    };

    getFilterOverlay = () => {
        return (
            <FilterOverlay>
                <FilterOverlayContent>
                    <div>
                        <div className={"d-flex"}>
                            <OwTitle title={"filters"} />
                            <div className={"ms-auto"}>
                                <OwButton className={"btn-danger"} title={"reset"} fn={this.resetFilters} />
                            </div>
                        </div>
                        <div>
                            <OwLabel title={"vendor"} isSupplierVendorOverride={true} supplierModel={this.props.supplierModel} />
                        </div>
                        <div>
                            <Typeahead
                                titleProp={"name"}
                                typeaheadItems={this.state.typeaheadVendors}
                                selectedItemFn={this.selectVendor}
                                typeaheadApiFn={this.typeaheadGetVendors}
                                isSelectableFn={() => true}
                                clearTypeahead={this.state.clearTypeahead}
                                clearTypeaheadCallback={this.clearTypeahead}
                                selectedItem={this.state.selectedVendor}
                                selectedItemTitleProp={"name"}
                            />
                        </div>
                    </div>
                    <div>
                        <div>
                            <OwLabel title={"order"} />
                        </div>
                        <div>
                            <OwFnInput fn={this.handleChange} name={"order"} value={this.state.order} />
                        </div>
                    </div>
                    <div>
                        <div>
                            <OwLabel title={"po_no"} />
                        </div>
                        <div>
                            <OwFnInput fn={this.handleChange} name={"po_no"} value={this.state.po_no} />
                        </div>
                    </div>
                    <div className={"mt-2"}>
                        <OwButton title={"search"} fn={this.getOrders} active={this.state.isActive} disableOnActive={true} />
                        <OwButton className={"ms-2 btn-danger"} title={"cancel"} fn={this.toggleFilters} />
                    </div>
                </FilterOverlayContent>
            </FilterOverlay>
        );
    };

    onPaginationChanged = (offset: number, limit: number) => {
        this.setState(
            {
                offset,
                limit,
            },
            () => {
                this.getOrders();
            }
        );
    };

    viewOnOrders = () => {
        let url = `/orders`;

        const paramsArr: string[] = ["filter_shipped=true", "filter_delivered=true", "filter_picked_up=true"];

        // We need to be sure of the order here so the back button doesn't take 2 clicks to return to the dashboard.
        if (this.state.po_no.length) {
            paramsArr.push(`po_no=${this.state.po_no}`);
        }

        if (this.state.order) {
            paramsArr.unshift(`order=${this.state.order}`);
        }

        if (this.state.selectedVendor) {
            paramsArr.unshift(`vendor=${this.state.selectedVendor.guid}`, `vendor_name=${this.state.selectedVendor.name}`);
        }

        url += "?" + paramsArr.join("&");

        this.props.history(url);
    };

    render() {
        if (
            !this.props.userModel ||
            !this.props.supplierModel ||
            this.props.userModel.supplierUserToken?.User_Type?.id === UserTypesEnum.SHIP_MGMT
        ) {
            return null;
        }

        let recentOrders = (
            <div
                className={"order-volume flex-grow d-flex flex-column align-items-center justify-content-center h-100"}
                style={{ minHeight: this.props.inEditor ? "" : "275px" }}>
                {this.props.inEditor ? (
                    <div className={""}>
                        <OwIconButton iconClass={"fas fa-trash-alt"} iconColorClass={"text-danger"} fn={this.props.removeWidget!} />
                    </div>
                ) : null}
                <OwLabel title={"dashboard.no_recent_orders_to_view"} />
                {this.props.inEditor ? null : this.props.userModel?.supplierUserToken?.access_type_id ===
                  AccessTypesEnum.READ_ONLY ? null : (
                    <Link title={"order"} to={"/orders?create=true"}>
                        <OwLabel className={"pointer"} title={"create_new"} leftIconClass={"fas fa-plus"} noBottomMargin={true} />
                    </Link>
                )}
            </div>
        );

        if (this.props.userModel && this.state.recentOrders.length) {
            const tableBody = this.state.recentOrders.map((order: IOrder, index: number) => {
                const do_not_ship_before = order.do_not_ship_before_date ? (
                    moment(order.do_not_ship_before_date).format("MM/DD/YYYY").toString()
                ) : (
                    <div>&nbsp;</div>
                );
                const last_updated = this.parseLastUpdated(order.updated_at);
                const lastUpdatedAriaLabel = this.props.supplierModel?.has_do_not_ship_before_date
                    ? "Do Not Ship Before"
                    : "Last Updated " + moment(last_updated, "MM/DD/YYYY").format("MMMM Do YYYY").toString();

                let vendorNameClientName = "";

                if (!_.isNil(order.Vendor)) {
                    vendorNameClientName = order.Vendor.name;
                    // @ts-expect-error
                } else if (!_.isNil(order.client_name)) {
                    // @ts-expect-error
                    vendorNameClientName = order.client_name;
                }

                const toUrl = UIService.getViewOrderUrl(order, this.props.supplierModel!);

                const hasApproveDecline = this.props.supplierModel?.has_approve_decline || false;

                let status = order.Order_State?.name.toLowerCase() || "";

                if (hasApproveDecline) {
                    if (order.approve_decline) {
                        status = order.approve_decline.toLowerCase();
                    }
                }

                return (
                    <tr key={index} role={"row"}>
                        <td className={"orderName"}>
                            {this.props.inEditor ? (
                                order.name
                            ) : (
                                <Link title={"order entry"} to={toUrl}>
                                    {order.name}
                                </Link>
                            )}
                        </td>
                        {this.props.userModel!.isSupplierUser ? (
                            <td tabIndex={0} className={"d-none d-lg-block vendorName"}>
                                <span title={vendorNameClientName}>{vendorNameClientName}</span>
                            </td>
                        ) : null}
                        <td className={"poName"} tabIndex={0}>
                            {order.po_no}
                        </td>
                        <td tabIndex={0} className={"orderState"}>
                            <OwTranslate defaultMessage={""} id={status} />
                        </td>
                        <td tabIndex={0} className={"d-none d-md-block"} aria-label={lastUpdatedAriaLabel}>
                            {this.props.supplierModel?.has_do_not_ship_before_date ? do_not_ship_before : last_updated}
                        </td>
                    </tr>
                );
            });

            recentOrders = (
                <div
                    className={
                        "RecentOrdersWidget recent-orders widget h-100 d-flex flex-column justify-content-start " +
                        (this.props.inEditor ? " overflow-hidden" : "")
                    }
                    style={{ minHeight: this.props.inEditor ? "" : "275px" }}>
                    {this.state.showFilterOverlay ? this.getFilterOverlay() : null}
                    <div className={"d-flex justify-content-between align-items-center w-100 ps-2 pe-2"}>
                        <OwTitle
                            tag={"h4"}
                            title={SupplierService.updateStringWithOrderNameOverride("recent_orders", this.props.supplierModel)}
                        />
                        <div className={"d-flex"}>
                            <OwButton title={"filter"} fn={this.toggleFilters} className={"ms-2 btn-link"}>
                                {this.state.filterCount ? <span>({this.state.filterCount})</span> : null}
                            </OwButton>
                            <OwButton
                                title={SupplierService.updateStringWithOrderNameOverride("orders", this.props.supplierModel)}
                                rightIconClass={"fa-duotone fa-up-right-from-square"}
                                className={"ms-2 btn-link"}
                                fn={this.viewOnOrders}
                            />
                        </div>
                        <span className={"ms-auto"}>
                            {this.props.inEditor ? (
                                <span>
                                    <OwLabel
                                        className={"pointer me-2"}
                                        title={"create_new"}
                                        leftIconClass={"fas fa-plus"}
                                        noBottomMargin={true}
                                    />
                                    <OwIconButton
                                        iconClass={"fas fa-trash-alt"}
                                        iconColorClass={"text-danger"}
                                        fn={this.props.removeWidget!}
                                    />
                                </span>
                            ) : this.props.supplierModel.settings?.screens?.orders?.hide_create ? null : this.props.userModel
                                  ?.supplierUserToken?.access_type_id === AccessTypesEnum.READ_ONLY ? null : (
                                <Link title={"order"} to={"/orders?create=true"}>
                                    <OwLabel
                                        className={"pointer"}
                                        title={"create_new"}
                                        leftIconClass={"fas fa-plus"}
                                        noBottomMargin={true}
                                    />
                                </Link>
                            )}
                        </span>
                    </div>
                    <div className={"table-responsive w-100"}>
                        <table
                            className={"table table-striped table-sm " + (UIService.isMainViewLight() ? "" : "table-dark")}
                            summary={"Recent Orders"}
                            role={"grid"}>
                            <thead>
                                <tr>
                                    <td className={"header"}>
                                        <OwLabel tabIndex={0} title={"name"} noBottomMargin={true} />
                                    </td>
                                    {this.props.userModel.isSupplierUser ? (
                                        <td className={"d-none d-lg-table-cell vendorNameTitle"}>
                                            <OwLabel
                                                tabIndex={0}
                                                title={"vendor"}
                                                supplierModel={this.props.supplierModel}
                                                isSupplierVendorOverride={true}
                                                noBottomMargin={true}
                                            />
                                        </td>
                                    ) : null}
                                    <td className={"w-25 header"}>
                                        <OwLabel tabIndex={0} title={"po_no"} noBottomMargin={true} />
                                    </td>
                                    <td className={"w-25 header"}>
                                        <OwLabel tabIndex={0} title={"status"} noBottomMargin={true} />
                                    </td>
                                    <td className={"d-none d-md-table-cell header"}>
                                        <OwLabel
                                            tabIndex={0}
                                            title={
                                                this.props.supplierModel.has_do_not_ship_before_date ? "do_not_ship_before" : "last_updated"
                                            }
                                            noBottomMargin={true}
                                        />
                                    </td>
                                </tr>
                            </thead>
                            <tbody>{tableBody}</tbody>
                        </table>
                    </div>

                    {this.state.totalOrders > 5 && this.state.hasInitialSearch ? (
                        <div className={"w-100"}>
                            <PaginationBar
                                hideLimit={true}
                                offset={this.state.offset}
                                limit={this.state.limit}
                                totalRecords={this.state.totalOrders}
                                onPaginationChanged={this.onPaginationChanged}
                            />
                        </div>
                    ) : null}
                </div>
            );
        }

        return recentOrders;
    }
}

export default RecentOrdersWidget;

const FilterOverlay = styled.div`
    width: 100%;
    height: 100%;
    position: absolute;
    z-index: 2;
    top: 0;
    padding-left: 15px;
    padding-right: 15px;
`;

const FilterOverlayContent = styled.div`
    width: 100%;
    height: 100%;
    background-color: var(--main-bg-color);
    color: var(--main-text-color);
    padding: 15px;
    border-radius: 1rem;
`;
